import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { fetchFallbackSpots, buildFallbackRecommendations } from "../lib/fallback-spots";
import {
  activityToSpot,
  fetchIslandActivities,
  getIslandSpotsSync,
} from "../lib/island-activities-api";
import { isProUser } from "../lib/patron";
import {
  applyTierToRecommendations,
  applyTierToSpots,
  filterAllInclusiveSpots,
} from "../lib/tier-spots";
import { formatSpotLocation } from "../lib/format-spot-location";
import { getCDNImage } from "../lib/cdnImage";
import { getSiteProfile } from "../lib/site-profile";
import { CARIBBEAN_ISLANDS } from "../lib/caribbeanIslands";
import { CARIBBEAN_ISLANDS_FALLBACK } from "../lib/caribbean-islands";
import { useScrolledNav } from "../hooks/useScrolledNav";
import MeetupSocialMap from "../components/maps/MeetupSocialMap";
import ProSearchHistory, { saveProSearchHistory } from "../components/discovery/ProSearchHistory";
import { 
  Search, MapPin, Star, Grid, List, Crown, Lock,
  Palmtree, UtensilsCrossed, Music, Waves, ShoppingBag,
  Sparkles, Hotel, Mountain, X, SlidersHorizontal,
  Heart, ArrowRight, Compass, Umbrella
} from "lucide-react";

const DiscoveryPage = () => {
  const { user } = useAuth();
  const siteProfile = getSiteProfile();
  const [searchParams, setSearchParams] = useSearchParams();
  const [spots, setSpots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [viewMode, setViewMode] = useState("grid");
  const [showFilters, setShowFilters] = useState(false);
  const [recommendations, setRecommendations] = useState(null);
  const [countries, setCountries] = useState(CARIBBEAN_ISLANDS_FALLBACK);
  const [allInclusiveOnly, setAllInclusiveOnly] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const searchSaveTimer = useRef(null);
  const isPro = isProUser(user);
  const islandNameByCode = useRef(
    Object.fromEntries(CARIBBEAN_ISLANDS.map((i) => [i.code, i.name]))
  );

  const country = searchParams.get("country") || "barbados";
  const category = searchParams.get("category") || "";
  const search = searchParams.get("search") || "";
  const highlightPin = searchParams.get("pin") || "";

  const categories = [
    { icon: Waves, label: "Beaches", value: "beach", color: "from-cyan-500 to-blue-600", bgColor: "bg-cyan-50" },
    { icon: UtensilsCrossed, label: "Restaurants", value: "restaurant", color: "from-orange-500 to-red-600", bgColor: "bg-orange-50" },
    { icon: Music, label: "Bars & Clubs", value: "bar", color: "from-purple-500 to-pink-600", bgColor: "bg-purple-50" },
    { icon: Sparkles, label: "Nightlife", value: "nightclub", color: "from-pink-500 to-rose-600", bgColor: "bg-pink-50" },
    { icon: Mountain, label: "Adventures", value: "adventure", color: "from-green-500 to-emerald-600", bgColor: "bg-green-50" },
    { icon: Palmtree, label: "Attractions", value: "attraction", color: "from-teal-500 to-cyan-600", bgColor: "bg-teal-50" },
    { icon: Hotel, label: "Hotels", value: "hotel", color: "from-amber-500 to-orange-600", bgColor: "bg-amber-50" },
    { icon: ShoppingBag, label: "Shopping", value: "shopping", color: "from-indigo-500 to-purple-600", bgColor: "bg-indigo-50" },
  ];

  useEffect(() => {
    axios
      .get(`${API}/countries`)
      .then((res) => {
        const list = res.data?.countries;
        if (Array.isArray(list) && list.length > 0) {
          setCountries(
            list.map((c) => ({
              code: c.code,
              name: c.name,
              flag: c.flag,
            }))
          );
        }
      })
      .catch(() => {});
  }, []);

  const shapeSpotList = useCallback(
    (rawSpots, totalAvailable, { reached = false } = {}) => {
      let list = applyTierToSpots(rawSpots, { isPro, limitReached: reached });
      if (allInclusiveOnly) list = filterAllInclusiveSpots(list);
      setSpots(list);
      setTotal(totalAvailable ?? list.length);
      setRecommendations(
        applyTierToRecommendations(buildFallbackRecommendations(list, country), { isPro })
      );
      return list;
    },
    [allInclusiveOnly, country, isPro]
  );

  const fetchSpots = useCallback(async () => {
    const instant = getIslandSpotsSync({ country, category, search, isPro, limitReached: false });
    shapeSpotList(instant.spots, instant.total);
    setLoading(false);
    setRefreshing(true);
    setLimitReached(false);

    const loadFromIslandCatalog = async (reached = false) => {
      const islandData = await fetchIslandActivities({
        country,
        category,
        search,
        isPro,
        limitReached: reached,
      });
      if (islandData.limit_reached) {
        setLimitReached(true);
        if (!isPro) setShowUpgradeModal(true);
        shapeSpotList(instant.spots, islandData.total_available || instant.total, { reached: true });
        return true;
      }
      const catalogSpots = (islandData.activities || []).map(activityToSpot);
      if (catalogSpots.length > 0) {
        shapeSpotList(catalogSpots, islandData.total_available || catalogSpots.length);
        return true;
      }
      return false;
    };

    const loadFromFallback = async (reached = false) => {
      const fallbackSpots = await fetchFallbackSpots({
        country,
        category,
        search,
        limit: isPro ? 50 : 20,
        isPro,
        limitReached: reached,
      });
      shapeSpotList(fallbackSpots, fallbackSpots.length, { reached });
    };

    try {
      const params = { limit: isPro ? 50 : 20 };
      if (country) params.country = country;
      if (category) params.category = category;
      if (search) params.search = search;

      const [spotsRes, recsRes] = await Promise.all([
        axios.get(`${API}/spots`, { params, withCredentials: true }),
        axios.get(`${API}/spots/recommendations/now`, { params: { country }, withCredentials: true }),
      ]);

      if (spotsRes.data?.limit_reached) {
        setLimitReached(true);
        if (!isPro) setShowUpgradeModal(true);
        await loadFromIslandCatalog(true);
        return;
      }

      const liveSpots = Array.isArray(spotsRes.data?.spots) ? spotsRes.data.spots : [];
      if (liveSpots.length === 0) {
        const fromIsland = await loadFromIslandCatalog(false);
        if (!fromIsland) await loadFromFallback(false);
      } else {
        let list = isPro ? liveSpots : applyTierToSpots(liveSpots, { isPro: false });
        if (allInclusiveOnly) list = filterAllInclusiveSpots(list);
        setSpots(list);
        setTotal(spotsRes.data.total || list.length);
        const liveRecs = recsRes?.data?.spots?.length
          ? applyTierToRecommendations(recsRes.data, { isPro })
          : applyTierToRecommendations(buildFallbackRecommendations(list, country), { isPro });
        setRecommendations(liveRecs);
      }
    } catch (error) {
      console.error("Fetch spots error:", error);
      const fromIsland = await loadFromIslandCatalog(false);
      if (!fromIsland) await loadFromFallback(false);
    } finally {
      setRefreshing(false);
    }
  }, [country, category, search, isPro, allInclusiveOnly, shapeSpotList]);

  useEffect(() => {
    fetchSpots();
  }, [fetchSpots]);

  useEffect(() => {
    if (!isPro) return undefined;
    if (searchSaveTimer.current) clearTimeout(searchSaveTimer.current);
    searchSaveTimer.current = setTimeout(() => {
      if (search || category || allInclusiveOnly) {
        saveProSearchHistory({
          query: search,
          country,
          category,
          filters: { all_inclusive: allInclusiveOnly },
        });
      }
    }, 1200);
    return () => {
      if (searchSaveTimer.current) clearTimeout(searchSaveTimer.current);
    };
  }, [search, country, category, allInclusiveOnly, isPro]);

  const updateFilter = (key, value) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    setSearchParams(params);
  };

  const applySearchFromHistory = ({ search: q, country: c, category: cat, allInclusive }) => {
    const params = new URLSearchParams();
    params.set("country", c || "barbados");
    if (cat) params.set("category", cat);
    if (q) params.set("search", q);
    setSearchParams(params);
    if (typeof allInclusive === "boolean") setAllInclusiveOnly(allInclusive);
  };

  const clearFilters = () => {
    setSearchParams({ country });
  };

  const hasFilters = category || search;
  const navScrolled = useScrolledNav(48);

  const islandLabel = islandNameByCode.current[country] || country;

  return (
    <div className="min-h-screen w-full max-w-full overflow-x-clip bg-gradient-to-b from-slate-50 to-white">
      {showUpgradeModal && !isPro && (
        <div className="fixed inset-0 z-[6000] flex items-center justify-center bg-black/70 backdrop-blur-md p-4">
          <div className="relative bg-white rounded-[2rem] p-8 md:p-12 max-w-lg w-full text-center shadow-2xl">
            <button
              type="button"
              onClick={() => setShowUpgradeModal(false)}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
            <Crown className="w-12 h-12 mx-auto mb-4 text-amber-500" />
            <h3 className="text-2xl font-serif font-bold mb-2">Daily discovery limit reached</h3>
            <p className="text-gray-600 mb-6">
              Explorer PRO unlocks unlimited searches, full island catalogs across all 15 destinations, and saved search memory.
            </p>
            <Link to="/premium">
              <Button className="w-full rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white py-6">
                Upgrade to Explorer PRO
              </Button>
            </Link>
          </div>
        </div>
      )}

      {/* Fixed Header — below the 35px ticker */}
      <header
        className={`fixed top-[var(--mansion-ticker-height)] left-0 right-0 z-[5000] transition-all duration-300 border-b ${
          navScrolled
            ? "bg-white/92 backdrop-blur-xl border-gray-100 shadow-sm"
            : "bg-white/55 backdrop-blur-md border-white/40"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3">
          <div className="flex items-center justify-between gap-4">
            {/* Logo & Back */}
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-orange-500 flex items-center justify-center">
                <Waves className="w-5 h-5 text-white" />
              </div>
              <span className="font-serif font-bold text-lg hidden sm:block bg-gradient-to-r from-cyan-600 to-orange-500 bg-clip-text text-transparent">
                {siteProfile.brandName}
              </span>
            </Link>

            {/* Search Bar */}
            <div className="flex-1 max-w-xl">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  value={search}
                  onChange={(e) => updateFilter("search", e.target.value)}
                  placeholder="Search places, experiences..."
                  className="w-full pl-12 pr-4 py-3 rounded-full border-gray-200 bg-gray-50 focus:bg-white transition-colors"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <button 
                onClick={() => setShowFilters(!showFilters)}
                className={`p-3 rounded-full transition-colors ${showFilters ? "bg-cyan-100 text-cyan-600" : "hover:bg-gray-100"}`}
              >
                <SlidersHorizontal className="w-5 h-5" />
              </button>
              <div className="hidden sm:flex items-center border rounded-full p-1">
                <button 
                  onClick={() => setViewMode("grid")}
                  className={`p-2 rounded-full transition-colors ${viewMode === "grid" ? "bg-gray-100" : ""}`}
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button 
                  onClick={() => setViewMode("list")}
                  className={`p-2 rounded-full transition-colors ${viewMode === "list" ? "bg-gray-100" : ""}`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content — extra 35px for ticker */}
      <main className="w-full max-w-full overflow-x-clip pt-[calc(var(--mansion-ticker-height)+var(--mansion-nav-height)+0.75rem)] pb-20">
        <div className="max-w-7xl mx-auto w-full px-4 md:px-8">
          {/* Hero Section */}
          <div className="mb-10">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {countries.map(c => (
                <button
                  key={c.code}
                  onClick={() => updateFilter("country", c.code)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    country === c.code 
                      ? "bg-gradient-to-r from-cyan-500 to-cyan-600 text-white shadow-lg" 
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  <span className="mr-1.5">{c.flag}</span>
                  {c.name}
                </button>
              ))}
            </div>
            <h1 className="text-3xl md:text-4xl font-serif font-bold mb-2">
              Explore {countries.find((c) => c.code === country)?.name || islandLabel}
            </h1>
            <p className="text-gray-600 flex flex-wrap items-center gap-2">
              <span>
                {total} places to discover • {category ? categories.find((c) => c.value === category)?.label : "All categories"}
              </span>
              {isPro && (
                <Badge className="bg-amber-100 text-amber-800 border-0">
                  <Crown className="w-3 h-3 mr-1" /> Explorer PRO
                </Badge>
              )}
              {refreshing && (
                <span className="text-xs text-cyan-600">Syncing live catalog…</span>
              )}
              {limitReached && !isPro && (
                <span className="text-xs text-amber-700">Preview mode — upgrade for full access</span>
              )}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setAllInclusiveOnly((v) => !v)}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${
                  allInclusiveOnly
                    ? "bg-emerald-600 text-white"
                    : "bg-emerald-50 text-emerald-800 border border-emerald-100"
                }`}
              >
                <Umbrella className="w-4 h-4" />
                All-inclusive experiences
              </button>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-6 mb-10">
            <div className="lg:col-span-2">
              <MeetupSocialMap
                variant="discovery"
                country={country}
                category={category}
                mapHeight={420}
                highlightPinId={highlightPin || undefined}
                showIslandPicker={false}
                showStats
                showFeaturedStrip
                isPro={isPro}
                onCountryChange={(code) => updateFilter("country", code)}
              />
            </div>
            <div className="space-y-4">
              <ProSearchHistory isPro={isPro} onApplySearch={applySearchFromHistory} />
              {allInclusiveOnly && (
                <p className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-2xl px-4 py-3">
                  All-inclusive filter applies to the list below. Map shows all verified geographic pins for this island.
                </p>
              )}
            </div>
          </div>

          {/* Category Filters */}
          <div className="mb-8 overflow-x-auto pb-2 max-w-full scrollbar-hide">
            <div className="flex gap-3 min-w-max w-max max-w-none">
              <button
                onClick={() => updateFilter("category", "")}
                className={`flex items-center gap-2 px-5 py-3 rounded-2xl font-medium transition-all ${
                  !category 
                    ? "bg-gradient-to-r from-slate-800 to-slate-900 text-white shadow-lg" 
                    : "bg-white border border-gray-200 text-gray-700 hover:border-gray-300"
                }`}
              >
                <Compass className="w-5 h-5" />
                All
              </button>
              {categories.map(cat => {
                const Icon = cat.icon;
                const isActive = category === cat.value;
                return (
                  <button
                    key={cat.value}
                    onClick={() => updateFilter("category", cat.value)}
                    className={`flex items-center gap-2 px-5 py-3 rounded-2xl font-medium transition-all ${
                      isActive 
                        ? `bg-gradient-to-r ${cat.color} text-white shadow-lg` 
                        : `bg-white border border-gray-200 text-gray-700 hover:border-gray-300`
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    {cat.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Active Filters */}
          {hasFilters && (
            <div className="flex items-center gap-2 mb-6 flex-wrap">
              <span className="text-sm text-gray-500">Filters:</span>
              {category && (
                <Badge className="bg-cyan-100 text-cyan-700 hover:bg-cyan-200 cursor-pointer" onClick={() => updateFilter("category", "")}>
                  {categories.find(c => c.value === category)?.label}
                  <X className="w-3 h-3 ml-1" />
                </Badge>
              )}
              {search && (
                <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-200 cursor-pointer" onClick={() => updateFilter("search", "")}>
                  "{search}"
                  <X className="w-3 h-3 ml-1" />
                </Badge>
              )}
              <button onClick={clearFilters} className="text-sm text-gray-500 hover:text-gray-700 underline">
                Clear all
              </button>
            </div>
          )}

          {/* AI Recommendations Banner */}
          {recommendations && !category && !search && (
            <div className="mb-10 p-6 rounded-2xl bg-gradient-to-r from-purple-500 to-pink-500 text-white">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-xl bg-white/20">
                  <Sparkles className="w-5 h-5" />
                </div>
                <span className="font-semibold">{recommendations.title || "AI Recommendations"}</span>
              </div>
              <p className="text-white/90 mb-4">{recommendations.context || "Based on current conditions"}</p>
              <div className="flex gap-2 flex-wrap">
                {recommendations.spots?.slice(0, 3).map(spot => (
                  <Link 
                    key={spot.spot_id}
                    to={`/spot/${spot.spot_id}`}
                    className="px-4 py-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors text-sm font-medium"
                  >
                    {spot.name}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading ? (
            <div className={`grid gap-6 ${viewMode === "grid" ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" : "grid-cols-1"}`}>
              {[...Array(8)].map((_, i) => (
                <div key={i} className="rounded-2xl bg-gray-100 animate-pulse h-72" />
              ))}
            </div>
          ) : spots.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gray-100 flex items-center justify-center">
                <Search className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Fresh premium destinations are loading</h3>
              <p className="text-gray-600 mb-6">Adjust your filters or refresh to reconnect with the Mansion catalog.</p>
              <Button onClick={clearFilters} className="rounded-full">
                Clear Filters
              </Button>
            </div>
          ) : (
            <div className={`grid gap-6 ${viewMode === "grid" ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" : "grid-cols-1"}`}>
              {spots.map((spot, idx) => (
                <SpotCard key={spot.spot_id} spot={spot} isPro={isPro} viewMode={viewMode} index={idx} />
              ))}
            </div>
          )}

          {/* PRO Upgrade Banner */}
          {!isPro && spots.length > 0 && (
            <div className="mt-16 p-8 md:p-12 rounded-3xl bg-gradient-to-br from-amber-400 via-orange-500 to-rose-500 text-white text-center">
              <Crown className="w-12 h-12 mx-auto mb-4" />
              <h3 className="text-2xl md:text-3xl font-serif font-bold mb-3">Unlock Hidden Gems</h3>
              <p className="text-white/90 mb-6 max-w-lg mx-auto">
                Upgrade to PRO for unlimited access, exclusive spots, advanced filters, and personalized recommendations.
              </p>
              <Link to="/premium">
                <Button className="px-8 py-6 rounded-full bg-white text-orange-600 font-semibold hover:bg-gray-50 shadow-xl">
                  Start Free Trial
                </Button>
              </Link>
            </div>
          )}
        </div>
      </main>

      {/* Filter Sidebar */}
      {showFilters && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setShowFilters(false)} />
          <div className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-white z-50 p-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-xl font-bold">Filters</h2>
              <button onClick={() => setShowFilters(false)} className="p-2 rounded-full hover:bg-gray-100">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* AI Preference Prompts */}
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold mb-3">What's your mood?</label>
                <div className="flex flex-wrap gap-2">
                  {["Relaxing", "Adventurous", "Romantic", "Party", "Cultural"].map(mood => (
                    <button key={mood} className="px-4 py-2 rounded-full border border-gray-200 hover:border-cyan-500 hover:bg-cyan-50 transition-colors text-sm">
                      {mood}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-3">Budget Range</label>
                <div className="flex flex-wrap gap-2">
                  {["Free", "$", "$$", "$$$", "$$$$"].map(budget => (
                    <button key={budget} className="px-4 py-2 rounded-full border border-gray-200 hover:border-cyan-500 hover:bg-cyan-50 transition-colors text-sm">
                      {budget}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-3">Time of Day</label>
                <div className="flex flex-wrap gap-2">
                  {["Morning", "Afternoon", "Evening", "Night"].map(time => (
                    <button key={time} className="px-4 py-2 rounded-full border border-gray-200 hover:border-cyan-500 hover:bg-cyan-50 transition-colors text-sm">
                      {time}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-3">Group Type</label>
                <div className="flex flex-wrap gap-2">
                  {["Solo", "Couple", "Family", "Friends", "Business"].map(group => (
                    <button key={group} className="px-4 py-2 rounded-full border border-gray-200 hover:border-cyan-500 hover:bg-cyan-50 transition-colors text-sm">
                      {group}
                    </button>
                  ))}
                </div>
              </div>

              {!isPro && (
                <div className="p-4 rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Crown className="w-5 h-5 text-amber-500" />
                    <span className="font-semibold text-amber-700">PRO Feature</span>
                  </div>
                  <p className="text-sm text-amber-600 mb-3">Unlock advanced AI filtering and personalized recommendations</p>
                  <Link to="/premium">
                    <Button size="sm" className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full">
                      Upgrade to PRO
                    </Button>
                  </Link>
                </div>
              )}
            </div>

            <div className="mt-8 pt-6 border-t flex gap-3">
              <Button variant="outline" className="flex-1 rounded-full" onClick={clearFilters}>
                Reset
              </Button>
              <Button className="flex-1 rounded-full bg-gradient-to-r from-cyan-500 to-cyan-600" onClick={() => setShowFilters(false)}>
                Apply Filters
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const SPOT_FALLBACKS = [
  'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800&q=80',
  'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=800&q=80',
  'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=800&q=80',
  'https://images.unsplash.com/photo-1519046904884-53103b34b206?w=800&q=80',
  'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80',
  'https://images.unsplash.com/photo-1505881502353-a1986add3762?w=800&q=80',
];

// Spot Card Component
function SpotCard({ spot, isPro, viewMode, index }) {
  const isLocked = spot.locked && !isPro;
  const locationText = formatSpotLocation(spot);
  const imageUrl = getCDNImage(
    spot.image_url || (spot.images && spot.images[0]),
    SPOT_FALLBACKS[index % SPOT_FALLBACKS.length]
  );
  
  if (viewMode === "list") {
    return (
      <Link 
        to={`/spot/${spot.spot_id}`}
        className="group flex gap-6 p-4 rounded-2xl bg-white border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all"
      >
        <div className="relative w-40 h-32 rounded-xl overflow-hidden flex-shrink-0">
          <img 
            src={imageUrl}
            alt={spot.name}
            className="w-full h-full object-cover"
          />
          {isLocked && (
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center">
              <Lock className="w-6 h-6 text-white" />
            </div>
          )}
        </div>
        <div className="flex-1 py-1">
          <div className="flex items-center gap-2 mb-2">
            {spot.is_featured && <Badge className="bg-amber-100 text-amber-700">Featured</Badge>}
            {spot.is_premium_content && <Badge className="bg-purple-100 text-purple-700"><Crown className="w-3 h-3 mr-1" />PRO</Badge>}
          </div>
          <h3 className="text-lg font-semibold mb-1 group-hover:text-cyan-600 transition-colors">{spot.name}</h3>
          <div className="flex items-center gap-3 text-sm text-gray-500 mb-2">
            <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{locationText}</span>
            {spot.rating && <span className="flex items-center gap-1"><Star className="w-4 h-4 text-amber-400 fill-amber-400" />{spot.rating}</span>}
          </div>
          <p className="text-gray-600 text-sm line-clamp-2">{spot.short_description || spot.description}</p>
        </div>
        <div className="flex items-center">
          <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-cyan-500 transition-colors" />
        </div>
      </Link>
    );
  }
  
  return (
    <Link 
      to={`/spot/${spot.spot_id}`}
      className="group relative block rounded-2xl overflow-hidden bg-white shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-1"
    >
      {/* Image */}
      <div className="relative h-56 overflow-hidden">
        <img 
          src={imageUrl}
          alt={spot.name}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

        {/* Lock Overlay */}
        {isLocked && (
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="text-center text-white p-4">
              <Lock className="w-8 h-8 mx-auto mb-2" />
              <p className="font-semibold">PRO Content</p>
            </div>
          </div>
        )}

        {/* Badges */}
        <div className="absolute top-3 left-3 flex gap-2">
          {spot.is_featured && (
            <Badge className="bg-amber-500 text-white border-0 shadow-lg">Featured</Badge>
          )}
          {spot.is_premium_content && (
            <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0 shadow-lg">
              <Crown className="w-3 h-3 mr-1" /> PRO
            </Badge>
          )}
        </div>

        {/* Favorite Button */}
        <button className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white/40">
          <Heart className="w-5 h-5 text-white" />
        </button>
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
          <MapPin className="w-4 h-4" />
          <span>{locationText}</span>
          {spot.rating && (
            <>
              <span className="mx-1">•</span>
              <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
              <span>{spot.rating}</span>
            </>
          )}
        </div>
        <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-cyan-600 transition-colors line-clamp-1">
          {spot.name}
        </h3>
        <p className="text-sm text-gray-600 line-clamp-2">{spot.short_description || (spot.description ? spot.description.substring(0, 100) : '')}</p>
      </div>
    </Link>
  );
}

export default DiscoveryPage;
